import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from "@/components/Header";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { MagicCard } from "@/components/ui/magic-card";
import { NavBar } from "@/components/ui/tubelight-navbar";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTheme } from "next-themes";
import { Eye, Calendar, Home, User, Briefcase, FileText, Ellipsis } from "lucide-react";
import { httpsCallable } from "firebase/functions";
import { functions } from "@/lib/firebase";
import { ExploreFilter, ExploreCard } from "@/utils/crosswordGenerator";
import { 
  SAMPLE_CROSSWORDS, 
  getSampleCrosswords, 
  getMoreSampleCrosswords, 
  simulateApiDelay 
} from "@/utils/sampleData";
import { useIsMobile } from "@/hooks/use-mobile";

type FilterType = "popular" | "recent";

const MiniCrosswordGrid = ({ grid }: { grid: boolean[][] }) => {
  return (
    <div className="w-full aspect-square max-w-40 mx-auto">
      <div 
        className="crossword-grid"
        style={{ 
          gridTemplateColumns: `repeat(${grid[0]?.length || 15}, 1fr)`,
          gridTemplateRows: `repeat(${grid.length || 15}, 1fr)`
        }}
      >
        {grid.map((row, i) => 
          row.map((cell, j) => (
            <div
              key={`${i}-${j}`}
              className={`
                crossword-cell
                ${cell ? 'filled' : 'empty'}
              `}
              style={{ 
                fontSize: '0.2rem',
                minHeight: '2.5px'
              }}
            >
              {cell ? ' ' : ''}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const ExplorePage = () => {
  const [filter, setFilter] = useState<FilterType>("popular");
  const [crosswords, setCrosswords] = useState<ExploreCard[]>([]);
  const [selectedTimeFilter, setSelectedTimeFilter] = useState("All Time");
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const [page, setPage] = useState(1);
  const [useSampleData, setUseSampleData] = useState(false);
  const { theme } = useTheme();
  const gradientColor = theme === "dark" ? "#3a3a3a" : "#e2d1c380";
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const loadMoreCrosswords = async () => {
    if (useSampleData) {
      // For sample data, just simulate loading more
      setIsLoadingMore(true);
      simulateApiDelay(() => {
        // Add more sample data if available
        const remainingSamples = getMoreSampleCrosswords(crosswords.length, 4);
        if (remainingSamples.length > 0) {
          setCrosswords(prev => [...prev, ...remainingSamples]);
        } else {
          setHasReachedEnd(true);
        }
        setIsLoadingMore(false);
      });
      return;
    }

    setIsLoadingMore(true);
    try {
      let getExploreCardsFunction;
      if(import.meta.env.MODE === 'development') {
        getExploreCardsFunction = httpsCallable<{ filter: ExploreFilter, page: number }, any>(functions, 'getExploreCardsFunction');
      }
      else {
        getExploreCardsFunction = httpsCallable<{ filter: ExploreFilter, page: number }, any>(functions, 'getExploreCardsFunction',{ limitedUseAppCheckTokens: true });
      }
      
      const nextPage = page + 1;
      
      // Determine current filter based on selected nav and time filter
      let currentFilter: ExploreFilter;
      if (filter === "recent") {
        currentFilter = ExploreFilter.Recent;
      } else {
        switch (selectedTimeFilter) {
          case 'Week':
            currentFilter = ExploreFilter.PopularWeek;
            break;
          case 'Month':
            currentFilter = ExploreFilter.PopularMonth;
            break;
          case 'Year':
            currentFilter = ExploreFilter.PopularYear;
            break;
          case 'All Time':
            currentFilter = ExploreFilter.PopularAllTime;
            break;
          default:
            currentFilter = ExploreFilter.PopularAllTime;
        }
      }
      
      const result = await getExploreCardsFunction({ 
        filter: currentFilter, 
        page: nextPage 
      });
      
      if (result.data && result.data.data && result.data.data.cards) {
        if (result.data.data.cards.length === 0) {
          setHasReachedEnd(true);
        } else {
          setCrosswords(prev => [...prev, ...result.data.data.cards]);
          setPage(nextPage);
        }
      } else {
        console.error('No cards data received');
        setHasReachedEnd(true);
      }
    } catch (error) {
      console.error('Error fetching explore cards:', error);
      setHasReachedEnd(true);
    } finally {
      setIsLoadingMore(false);
    }
  };

  // Function to call getExploreCardsFunction
  const handleRecentClick = async () => {
    if (useSampleData) {
      setIsLoading(true);
      setPage(1);
      setFilter("recent");
      setHasReachedEnd(false);
      // Simulate loading delay
      simulateApiDelay(() => {
        setCrosswords(getSampleCrosswords('recent', 8));
        setIsLoading(false);
      });
      return;
    }

    setIsLoading(true);
    setPage(1);
    setFilter("recent");
    setHasReachedEnd(false);
    try {
      let getExploreCardsFunction;
      if(import.meta.env.MODE === 'development') {
        getExploreCardsFunction = httpsCallable<{ filter: ExploreFilter, page: number }, any>(functions, 'getExploreCardsFunction');
      }
      else {
        getExploreCardsFunction = httpsCallable<{ filter: ExploreFilter, page: number }, any>(functions, 'getExploreCardsFunction',{ limitedUseAppCheckTokens: true });
      }
      
      const result = await getExploreCardsFunction({ 
        filter: ExploreFilter.Recent, 
        page: 1 
      });
      
      
      if (result.data && result.data.data && result.data.data.cards) {
        setCrosswords(result.data.data.cards);
      } else {
        console.error('No cards data received');
        setCrosswords([]);
      }
    } catch (error) {
      console.error('Error fetching explore cards:', error);
      setCrosswords([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle popular filter changes
  const handlePopularFilterChange = async (timeFilter: string) => {
    if (useSampleData) {
      setIsLoading(true);
      setPage(1);
      setFilter("popular");
      setHasReachedEnd(false);
      // Simulate loading delay
      simulateApiDelay(() => {
        setCrosswords(getSampleCrosswords('popular', 8));
        setIsLoading(false);
      });
      return;
    }

    setIsLoading(true);
    setPage(1);
    setFilter("popular");
    setHasReachedEnd(false);
    try {
      let getExploreCardsFunction;
      if(import.meta.env.MODE === 'development') {
        getExploreCardsFunction = httpsCallable<{ filter: ExploreFilter, page: number }, any>(functions, 'getExploreCardsFunction');
      }
      else {
        getExploreCardsFunction = httpsCallable<{ filter: ExploreFilter, page: number }, any>(functions, 'getExploreCardsFunction',{ limitedUseAppCheckTokens: true });
      }
      
      // Map dropdown selection to ExploreFilter
      let filter: ExploreFilter;
      switch (timeFilter) {
        case 'Week':
          filter = ExploreFilter.PopularWeek;
          break;
        case 'Month':
          filter = ExploreFilter.PopularMonth;
          break;
        case 'Year':
          filter = ExploreFilter.PopularYear;
          break;
        case 'All Time':
          filter = ExploreFilter.PopularAllTime;
          break;
        default:
          filter = ExploreFilter.PopularAllTime;
      }
      
      const result = await getExploreCardsFunction({ 
        filter: filter, 
        page: 1 
      });
      
      if (result.data && result.data.data && result.data.data.cards) {
        setCrosswords(result.data.data.cards);
      } else {
        console.error('No cards data received');
        setCrosswords([]);
      }
    } catch (error) {
      console.error('Error fetching explore cards:', error);
      setCrosswords([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Function to handle time filter selection
  const handleTimeFilterSelect = (timeFilter: string) => {
    setSelectedTimeFilter(timeFilter);
    handlePopularFilterChange(timeFilter);
  };

  // Function to handle sample data toggle
  const handleSampleDataToggle = (enabled: boolean) => {
    setUseSampleData(enabled);
    setPage(1);
    setHasReachedEnd(false);
    
    if (enabled) {
      // Load sample data
      setIsLoading(true);
      simulateApiDelay(() => {
        setCrosswords(getSampleCrosswords('popular', 8));
        setIsLoading(false);
      });
    } else {
      // Load real data
      handlePopularFilterChange(selectedTimeFilter);
    }
  };

  // Format date for display
  const formatDate = (date: Date | any) => {
    try {
      // Handle Firestore timestamp objects
      if (date && typeof date.toDate === 'function') {
        date = date.toDate();
      }
      
      // Handle string timestamps
      if (typeof date === 'string') {
        date = new Date(date);
      }
      
      // Handle number timestamps (Unix timestamp)
      if (typeof date === 'number') {
        date = new Date(date * 1000);
      }
      
      // Ensure we have a valid Date object
      if (!(date instanceof Date) || isNaN(date.getTime())) {
        console.warn('Invalid date format:', date);
        return 'Unknown date';
      }
      
      return date.toLocaleDateString('en-US', { 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric' 
      });
    } catch (error) {
      console.error('Error formatting date:', error, 'Date value:', date);
      return 'Invalid date';
    }
  };

  const navItems = [
    { 
      name: 'Most Popular', 
      url: '#', 
      icon: Home,
      onClick: () => handlePopularFilterChange(selectedTimeFilter),
      dropdown: {
        items: ['Week', 'Month', 'Year', "All Time"],
        selectedItem: selectedTimeFilter,
        onItemSelect: handleTimeFilterSelect
      }
    },
    { 
      name: 'Recent', 
      url: '#', 
      icon: FileText,
      onClick: handleRecentClick
    }
  ];

  useEffect(() => {
    if (useSampleData) {
      // Load sample data on initial render if toggle is enabled
      setIsLoading(true);
      simulateApiDelay(() => {
        setCrosswords(getSampleCrosswords('popular', 8));
        setIsLoading(false);
      });
    } else {
      // Load real data
      handlePopularFilterChange("All Time");
    }
  }, [useSampleData]);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      
      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-extrabold tracking-tight mb-4">
              Explore Crosswords
            </h1>
            
            {useSampleData && (
              <div className="bg-yellow-100 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg px-4 py-2 mb-4">
                <p className="text-yellow-800 dark:text-yellow-200 text-sm">
                  🧪 Sample data mode enabled - Using test data instead of real API calls
                </p>
              </div>
            )}
          </div>
          
          {/* Centered Navigation Bar with Dropdown - Sticky on Mobile */}
          <div className={`w-full flex justify-center items-center gap-4 mb-8 ${isMobile ? 'sticky top-0 z-50 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b border-border/40 py-4' : ''}`}>
            <NavBar 
              items={navItems}
              className="relative"
            />
          </div>
        
          {/* Cards Section - Different behavior on mobile vs desktop */}
          {isMobile ? (
            // Mobile: Non-scrollable, full list
            <div className="space-y-6">
              {isLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="text-lg">Loading crosswords...</div>
                </div>
              ) : crosswords.length === 0 ? (
                <div className="flex justify-center items-center h-64">
                  <div className="text-lg text-primary">
                    {"No crosswords found"}
                  </div>
                </div>
              ) : (
                <div className="space-y-6">
                  {crosswords.map((crossword) => (
                    <MagicCard 
                      key={crossword.uuid}
                      className="cursor-pointer transition-all hover:shadow-2xl shadow-lg bg-opacity-90 backdrop-blur-sm group"
                      gradientColor={gradientColor}
                      gradientOpacity={0.6}
                      onClick={() => navigate(`/share/${crossword.uuid}`)}
                    >
                      <Card className="border-none bg-transparent flex flex-col">
                        <CardHeader className="pb-4 pt-6">
                          <CardTitle className="text-2xl font-bold text-center leading-tight group-hover:text-primary transition-colors line-clamp-2 h-16 flex items-center justify-center">
                            {crossword.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="flex flex-col items-center space-y-4 px-6">
                          <MiniCrosswordGrid grid={crossword.grid} />
                          <div className="flex items-center justify-between w-full text-sm text-gray-600">
                            <div className="flex items-center space-x-2">
                              <Calendar className="w-4 h-4" />
                              <span className="font-medium">{formatDate(crossword.timestamp)}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Eye className="w-4 h-4" />
                              <span className="font-medium">{crossword.view_count.toLocaleString()}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </MagicCard>
                  ))}
                </div>
              )}
              
              {crosswords.length > 0 && page < 10 && crosswords.length % 12 === 0 && (
                <div className="flex justify-center mt-8 mb-4">
                  {isLoadingMore ? (
                    <button 
                      disabled
                      className="px-6 py-3 rounded-lg bg-primary text-primary-foreground opacity-50 cursor-not-allowed font-semibold shadow-md"
                    >
                      Loading crosswords...
                    </button>
                  ) : hasReachedEnd ? (
                    <button 
                      disabled
                      className="px-6 py-3 rounded-lg bg-primary text-primary-foreground opacity-50 cursor-not-allowed font-semibold shadow-md"
                    >
                      No more crosswords available
                    </button>
                  ) : (
                    <button 
                      onClick={loadMoreCrosswords}
                      className="px-6 py-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-semibold shadow-md hover:shadow-lg"
                    >
                      Load More Crosswords
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : (
            // Desktop: Scrollable area
            <ScrollArea className="h-[700px] rounded-md">
              {isLoading ? (
                <div className="flex justify-center items-center h-64">
                  <div className="text-lg">Loading crosswords...</div>
                </div>
              ) : crosswords.length === 0 ? (
                <div className="flex justify-center items-center h-64">
                  <div className="text-lg text-primary">
                    {"No crosswords found"}
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-8 p-1">
                  {crosswords.map((crossword) => (
                    <MagicCard 
                      key={crossword.uuid}
                      className="cursor-pointer transition-all hover:shadow-2xl shadow-lg bg-opacity-90 backdrop-blur-sm h-full group"
                      gradientColor={gradientColor}
                      gradientOpacity={0.6}
                      onClick={() => navigate(`/share/${crossword.uuid}`)}
                    >
                      <Card className="border-none bg-transparent h-full flex flex-col">
                        <CardHeader className="pb-4 pt-6">
                          <CardTitle className="text-2xl font-bold text-center leading-tight group-hover:text-primary transition-colors line-clamp-2 h-16 flex items-center justify-center">
                            {crossword.title}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="flex-1 flex flex-col items-center space-y-4 px-6">
                          <MiniCrosswordGrid grid={crossword.grid} />
                          <div className="flex items-center justify-between w-full text-sm text-gray-600">
                            <div className="flex items-center space-x-2">
                              <Calendar className="w-4 h-4" />
                              <span className="font-medium">{formatDate(crossword.timestamp)}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                              <Eye className="w-4 h-4" />
                              <span className="font-medium">{crossword.view_count.toLocaleString()}</span>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </MagicCard>
                  ))}
                </div>
              )}
              
              {crosswords.length > 0 && page < 10 && crosswords.length % 12 === 0 && (
                <div className="flex justify-center mt-8 mb-4">
                  {isLoadingMore ? (
                    <button 
                      disabled
                      className="px-6 py-3 rounded-lg bg-primary text-primary-foreground opacity-50 cursor-not-allowed font-semibold shadow-md"
                    >
                      Loading crosswords...
                    </button>
                  ) : hasReachedEnd ? (
                    <button 
                      disabled
                      className="px-6 py-3 rounded-lg bg-primary text-primary-foreground opacity-50 cursor-not-allowed font-semibold shadow-md"
                    >
                      No more crosswords available
                    </button>
                  ) : (
                    <button 
                      onClick={loadMoreCrosswords}
                      className="px-6 py-3 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors font-semibold shadow-md hover:shadow-lg"
                    >
                      Load More Crosswords
                    </button>
                  )}
                </div>
              )}
            </ScrollArea>
          )}
        </div>
      </main>
      
      <footer className="mt-auto py-2 sm:py-3 bg-secondary/80" style={{ color: '#283618' }}>
        <div className="container mx-auto px-4 text-center text-sm text-primary">
          <p>© 2025 Crossable. All rights reserved.</p>
          <p className="mt-2">Business inquiries, feedback, or bug reports? Reach us at crossabledev@gmail.com</p>
        </div>
      </footer>
    </div>
  );
};

export default ExplorePage;
